import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, API } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { PatientLayout, HelpDrawer, PatientLoading, PatientError } from '@/components/PatientLayout';
import { CheckCircle2, Download, Mail, MapPin, Share2, Printer } from 'lucide-react';
import { useLang } from '@/lib/i18n';

export default function PatientView() {
  const { t } = useLang();
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  // Hand the PDF to the phone's own share sheet (Mail, Messages, AirDrop, the
  // HR portal's upload). Only where the browser has one; otherwise the dialog
  // explains the ways to get it to the employer (full-flow review 2026-09-06 §2).
  const share = async () => {
    try {
      if (navigator.share && navigator.canShare) {
        const res = await fetch(`${API}/patient/by-token/${token}/pdf`);
        if (!res.ok) throw new Error(`pdf ${res.status}`); // never share an error page as a "PDF"
        const blob = await res.blob();
        // Filename is deliberately generic: it lands in other apps' recents.
        const file = new File([blob], 'medical-leave-document.pdf', { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: t('docs.readyTitle') });
          return;
        }
      }
    } catch (e) {
      if (e?.name === 'AbortError') return; // they closed the sheet — nothing to explain
    }
    setShareOpen(true);
  };

  const load = useCallback(async () => {
    try {
      const r1 = await api.get(`/patient/by-token/${token}/document`);
      setInfo(r1.data);
      const r2 = await api.get(`/patient/by-token/${token}`);
      setDetails(r2.data);
    } catch (e) {
      // An expired or unknown link is final — show it, rather than "Loading…"
      // for ever. Anything else is transient during polling.
      const code = e?.response?.status;
      if (code === 404 || code === 410) setError(e.response.data?.detail || true);
    }
  }, [token]);

  useEffect(() => {
    if (error) return undefined;   // a dead link stays dead: stop polling it
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load, error]);

  if (error) return <PatientError message={typeof error === 'string' ? error : t('layout.linkNotFound')} />;
  if (!info) return <PatientLoading />;

  const ready = info.status === 'complete' || info.available;
  const practice = details?.practice;
  const deliveryMethod = details?.submission?.delivery?.method || 'Email';

  return (
    <PatientLayout
      practice={practice}
      right={<HelpDrawer practice={practice} token={token} triggerTestId="patient-docs-need-help-button" />}
      footer={
        ready ? (
          <Button asChild className="w-full h-14 rounded-xl text-base font-semibold" data-testid="patient-download-pdf-button">
            <a href={`${API}/patient/by-token/${token}/pdf`} target="_blank" rel="noreferrer">
              <Download className="h-5 w-5 mr-1.5" /> {t('docs.download')}
            </a>
          </Button>
        ) : (
          <Button variant="outline" className="w-full h-14 rounded-xl text-base" onClick={() => navigate(`/p/${token}/status`)} data-testid="patient-docs-track-status-button">
            <MapPin className="h-5 w-5 mr-1.5" /> {t('welcome.track')}
          </Button>
        )
      }
    >
      <div className="space-y-5">
        {ready ? (
          <>
            <div className="pt-2">
              <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center mb-4">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="patient-hero-title text-3xl font-bold leading-tight" data-testid="patient-docs-ready-title">{t('docs.readyTitle')}</h1>
              <p className="text-[15px] text-muted-foreground leading-7 mt-2">
                {t('docs.readySub')}
              </p>
            </div>

            <Card className="rounded-2xl">
              <CardContent className="p-5 space-y-3">
                <div className="font-semibold text-[16px]">{t('docs.whoGets')}</div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="text-sm text-muted-foreground leading-6" data-testid="patient-docs-delivery-info">
                    {/* Same truth as the status page (patient_public.py): only a
                        fax transmits the packet. An "Email" delivery sends the
                        employer a PHI-free heads-up and the patient still hands
                        the document over (full-flow review 2026-09-06 §1.1). */}
                    {deliveryMethod.toLowerCase().includes('fax')
                      ? t('docs.sentByTeam', { method: t('method.fax') })
                      : deliveryMethod === 'Patient-collected'
                        ? t('docs.selfDeliver')
                        : t('docs.emailNotice')}
                  </div>
                </div>
                <Button variant="outline" className="w-full h-12 rounded-xl text-[15px]" onClick={share} data-testid="patient-docs-share-button">
                  <Share2 className="h-4 w-4 mr-1.5" /> {t('docs.shareQ')}
                </Button>
                <Dialog open={shareOpen} onOpenChange={setShareOpen}>
                  <DialogContent data-theme="patient" className="rounded-2xl">
                    <DialogHeader>
                      <DialogTitle>{t('docs.shareTitle')}</DialogTitle>
                      <DialogDescription className="text-[15px] leading-6">{t('docs.shareSub')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm leading-6">
                      <div className="flex items-start gap-3"><Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" /><span><span className="font-semibold">{t('docs.shareEmail')}</span> {t('docs.shareEmailText')}</span></div>
                      <div className="flex items-start gap-3"><Printer className="h-5 w-5 text-primary mt-0.5 shrink-0" /><span><span className="font-semibold">{t('docs.sharePrint')}</span> {t('docs.sharePrintText')}</span></div>
                      <div className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" /><span><span className="font-semibold">{t('docs.shareDone')}</span> {t('docs.shareDoneText')}</span></div>
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button className="h-12 rounded-xl w-full" data-testid="patient-docs-share-close">{t('docs.gotIt')}</Button>
                      </DialogClose>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            <button type="button" onClick={() => navigate(`/p/${token}/status`)} className="text-[15px] text-primary font-medium inline-flex items-center gap-1.5" data-testid="patient-docs-status-link">
              <MapPin className="h-4 w-4" /> {t('docs.timeline')}
            </button>
          </>
        ) : (
          <>
            <div className="pt-2">
              <h1 className="patient-hero-title text-3xl font-bold leading-tight">{t('docs.almost')}</h1>
              <p className="text-[15px] text-muted-foreground leading-7 mt-2" data-testid="patient-docs-pending-text">
                {t('docs.pending')}
              </p>
            </div>
            <Card className="rounded-2xl">
              <CardContent className="p-5 text-sm text-muted-foreground leading-6">
                {t('docs.trackHint', { track: `“${t('welcome.track')}”` })}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PatientLayout>
  );
}
